import { isEmpty } from 'lodash';
import {
  AccountConfig as AccountConfigType,
  AccountMeta,
  RawAccountInitialData,
} from 'api/account';
import { AccountFields } from 'api/fields/fields';
import { PermissionSetDefinition } from 'api/rbac/rbac-types';
import { ResourceManagementConfig } from 'api/resource-management/types';
import { ApiConfiguration } from 'api/timesheets/configuration/api-configuration';
import { getCurrentApiHost } from 'config/environment';
import { MembersResponse, MembersV2Response } from 'data/members/api';
import { CustomerPreview } from 'services';
import { TimerApi } from 'services/timer';

export enum InitWorkerRequestType {
  GetMeta = 'GET_META',
  Init = 'INIT',
  TaskFields = 'TASK_FIELDS',
  Fields = 'FIELDS',
  Members = 'MEMBERS',
  RmConfig = 'RM_CONFIG',
  TimesheetConifg = 'TS_CONFIG',
  ProjectPageMembers = 'PROJECT_PAGE_MEMBERS',
  AccountConfig = 'ACCOUNT_CONFIG',
  PermissionSetDefinitions = 'PERMISSION_SET_DEFINITIONS',
}

type TypeName =
  | InitWorkerRequestType.GetMeta
  | InitWorkerRequestType.Init
  | InitWorkerRequestType.TaskFields
  | InitWorkerRequestType.Fields
  | InitWorkerRequestType.Members
  | InitWorkerRequestType.TimesheetConifg
  | InitWorkerRequestType.RmConfig
  | InitWorkerRequestType.ProjectPageMembers
  | InitWorkerRequestType.AccountConfig
  | InitWorkerRequestType.PermissionSetDefinitions;

type ObjectType<T> = T extends 'TS_CONFIG'
  ? ApiConfiguration
  : T extends 'GET_META'
  ? AccountMeta
  : T extends 'INIT'
  ? RawAccountInitialData
  : T extends 'TASK_FIELDS'
  ? AccountFields
  : T extends 'FIELDS'
  ? AccountFields[]
  : T extends 'MEMBERS'
  ? MembersV2Response
  : T extends 'RM_CONFIG'
  ? ResourceManagementConfig
  : T extends 'PROJECT_PAGE_MEMBERS'
  ? MembersResponse
  : T extends 'ACCOUNT_CONFIG'
  ? AccountConfigType
  : T extends 'PERMISSION_SET_DEFINITIONS'
  ? Array<PermissionSetDefinition>
  : never;

// this is used so that when refetching data from the api, it should return new values
const invalidateKey = {
  [InitWorkerRequestType.Fields]: false,
  [InitWorkerRequestType.TaskFields]: false,
  [InitWorkerRequestType.GetMeta]: false,
  [InitWorkerRequestType.Init]: false,
  [InitWorkerRequestType.Members]: false,
  [InitWorkerRequestType.ProjectPageMembers]: false,
  [InitWorkerRequestType.RmConfig]: false,
  [InitWorkerRequestType.TimesheetConifg]: false,
  [InitWorkerRequestType.AccountConfig]: false,
  [InitWorkerRequestType.PermissionSetDefinitions]: false,
};

const apiPathMap: Record<TypeName, string> = {
  [InitWorkerRequestType.GetMeta]: '/api/v1/accounts/get-meta',
  [InitWorkerRequestType.Init]: '/api/v1/home/init',
  [InitWorkerRequestType.AccountConfig]: '/api/v1/home/configuration',
  [InitWorkerRequestType.TaskFields]: '/api/v1/fields/task',
  [InitWorkerRequestType.Fields]: '/api/v1/fields',
  [InitWorkerRequestType.Members]: '/api/v1/members',
  [InitWorkerRequestType.RmConfig]: '/api/v1/resource-management/config',
  [InitWorkerRequestType.TimesheetConifg]: '/api/v1/timesheets/config',
  [InitWorkerRequestType.ProjectPageMembers]: '/api/v1/members',
  [InitWorkerRequestType.PermissionSetDefinitions]:
    '/api/v1/permission-set/get-definition',
};

const getData = async <T extends TypeName>(key: TypeName) => {
  // In test environment, make the HTTP call directly instead of using the web worker
  if (process.env.NODE_ENV === 'test') {
    const apiKey = localStorage.getItem('__api_key');
    const headers: Record<string, string> = {};

    if (apiKey) {
      const [, apiKeyValue] = JSON.parse(apiKey);

      headers['api-key'] = apiKeyValue;
    }

    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    const resp = await fetch(`${API_HOST}${apiPathMap[key]}`, { headers });

    return resp.json() as Promise<ObjectType<T>>;
  }

  const [isPreview] = await CustomerPreview.isPreviewMode();

  return new Promise<ObjectType<T>>((resolve, rej) => {
    const isInvalidate = invalidateKey[key];
    const timer = new TimerApi(30000);

    if (window.initWorker == null) {
      throw new Error('Worker is not available');
    }

    window.initWorker.postMessage({
      type: 'getData',
      request: key,
      isInvalidate,
      isCustomerPreview:
        window.location.search.includes('customer-preview=true') || isPreview,
      data: {
        apiKey:
          localStorage.getItem('__api_key') != null
            ? JSON.parse(localStorage.getItem('__api_key') as string)[1]
            : undefined,
        domain: `${getCurrentApiHost()}/api`,
      },
    });
    timer.start();
    timer.onElapsed(() => {
      rej();
    });
    window.initWorker.addEventListener('message', (e) => {
      if (e.data.type === key && e.data.respData === 'ERROR_OCCURRED') {
        timer.stop();
        rej();
      } else if (e.data.type === key && !isEmpty(e.data.respData)) {
        timer.stop();
        resolve(JSON.parse(e.data.respData));
        // this is removed so that when refetching data from the worker should return same values
        // invalidateKey[key] = true;
      }
    });
  });
};

const setData = async () => {
  if (window.initWorker == null) {
    return;
  }
  const isApiKeyPresent = localStorage.getItem('__api_key') != null;

  const [isPreview] = await CustomerPreview.isPreviewMode();

  window.initWorker.addEventListener('message', () => {
    window.initWorker.postMessage({
      type: isApiKeyPresent ? 'setData' : 'setMetaData',
      isInvalidate: false,
      isCustomerPreview:
        window.location.search.includes('customer-preview=true') || isPreview,
      data: {
        apiKey: isApiKeyPresent
          ? JSON.parse(localStorage.getItem('__api_key') as string)[1]
          : undefined,
        domain: `${getCurrentApiHost()}/api`,
      },
    });
  });
};

export const InitWorkerApi = {
  getData,
  setData,
};
